import { v4 } from "uuid";

import { PREVIEW_IFRAME_ID } from "@src/features/experienceBuilder/constants";
import { messageProxyFactory } from "@src/features/experienceBuilder/messageProxyFactory";

// A client tool is a single round-trip to the iframe plugin, but a heavy page's
// snapdom render (or a slow `execute_js`) can take a few seconds, so allow
// generous headroom.
const CLIENT_TOOL_TIMEOUT_MS = 20_000;

/**
 * Run a parked `execution: "client"` tool in the preview iframe and await its
 * result. Forwards `{ tool, input }` over `ig-clientTool` and resolves the
 * matching `ig-clientToolResult`, correlated by `requestId`. Generic — the
 * plugin dispatches by tool name — so every client-produced-result tool (inspect
 * today, locate/others later) uses this one transport. Returns the tool's raw
 * output; the caller shapes it into the tool-result the model sees.
 *
 * Serialized upstream: the LLM node parks one client tool call at a time, so
 * these never overlap — which matters because the transport keeps a single
 * listener per message type and couldn't disambiguate two calls in flight.
 */
export const requestClientTool = (
  tool: string,
  input: unknown
): Promise<unknown> =>
  new Promise<unknown>((resolve, reject) => {
    const messageProxy = messageProxyFactory.get(PREVIEW_IFRAME_ID);
    if (!messageProxy) {
      reject(new Error("Preview iframe unavailable for client tool"));
      return;
    }

    const requestId = v4();
    let done = false;
    let deadline: ReturnType<typeof setTimeout> | undefined;

    const finish = (
      action: { ok: true; output: unknown } | { ok: false; error: string }
    ) => {
      if (done) {
        return;
      }
      done = true;
      if (deadline !== undefined) {
        clearTimeout(deadline);
      }
      if (action.ok) {
        resolve(action.output);
      } else {
        reject(new Error(action.error));
      }
    };

    messageProxy.on("ig-clientToolResult", (payload) => {
      const p = payload as {
        requestId: string;
        output?: unknown;
        error?: string;
      };
      if (p.requestId !== requestId) {
        return;
      } // not our call
      if (p.error) {
        finish({ ok: false, error: p.error });
      } else {
        finish({ ok: true, output: p.output });
      }
    });

    messageProxy.send("ig-clientTool", { requestId, tool, input });
    deadline = setTimeout(
      () => finish({ ok: false, error: `Client tool "${tool}" timed out` }),
      CLIENT_TOOL_TIMEOUT_MS
    );
  });
